import React, { type ReactElement, useEffect } from 'react';
import {
	getErrorAttributes,
	isClientNetworkError,
	isHttpClientErrorResponse,
} from '@jira/assets-logging/src/common/utils.tsx';
import PageDI from '@atlaskit/page';
import {
	ContextualAnalyticsData,
	SCREEN,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { TrackerCallbacks } from '@atlassian/jira-common-experience-tracking-viewing/src/view/common/types.tsx';
import { ServiceDeskAnalyticsContext } from '@atlassian/jira-servicedesk-analytics/src/ui/servicedesk-analytics-context/index.tsx';
import {
	toPackageName,
	type AppName,
} from '@atlassian/jira-servicedesk-common/src/utils/app-names/index.tsx';

import { AsyncCmdbUpsellPage } from '@atlassian/jira-cmdb-upsell-page/src/async.tsx';
import {
	GenericPermissionErrorPageAsync,
	NotFoundErrorPageAsync,
} from '@atlassian/jira-error-pages/src/async.tsx';
import { CmdbArchitectureContainer } from '@atlassian/jira-servicedesk-cmdb-architecture/src/ui/index.tsx';
import { isCMDBEnabledInEnvironment } from '@atlassian/jira-servicedesk-cmdb-fedramp/src/index.tsx';
import {
	useInsightVisibility,
	useWorkspaceContext,
} from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/controllers/workspace-context/index.tsx';
import {
	getUserJsmLicenceState,
	isLicensedPremiumUser,
} from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/services/resources/get-data/index.tsx';
import type { WorkspaceId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import type { PageWrapper } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-style/index.tsx';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { Redirect } from '@atlassian/react-resource-router';
import { InsightGlobalConfigContainer } from '@atlassian/jira-servicedesk-insight-global-configuration-store/src/services/index.tsx';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useAssetsDataManagerUserAuth } from '@atlassian/jira-assets-data-manager-common/src/hooks/assets-data-manager-user-auth/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { NoAccessErrorPage } from './no-access-error-page/index.tsx';

type LegacyProps = {
	children: (arg1: { workspaceId: WorkspaceId }) => ReactElement;
};

type Props = {
	appName: AppName;
	children: (arg1: { workspaceId: WorkspaceId }) => ReactElement;
	pageStyle?: PageWrapper;
	onExperienceFailure?: TrackerCallbacks['onExperienceFailure'];
};

export const AssetsAppBase = ({
	appName,
	children,
	pageStyle = PageDI,
	onExperienceFailure,
}: Props) => (
	<>
		<SubProductUpdater subProduct="serviceDesk" />
		<ServiceDeskAnalyticsContext>
			<ContextualAnalyticsData sourceName={toPackageName(appName)} sourceType={SCREEN}>
				<AssetsAppBaseInternal pageStyle={pageStyle} onExperienceFailure={onExperienceFailure}>
					{children}
				</AssetsAppBaseInternal>
			</ContextualAnalyticsData>
		</ServiceDeskAnalyticsContext>
	</>
);

export const AssetsAppBaseInternal = ({
	children,
	pageStyle: PageWrapper,
	onExperienceFailure,
}: {
	children: (arg1: { workspaceId: WorkspaceId }) => ReactElement;
	pageStyle: PageWrapper;
	onExperienceFailure?: TrackerCallbacks['onExperienceFailure'];
}) => {
	const { workspaceId, error, loading } = useWorkspaceContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const tenantContext = useTenantContext();
	const licenceState = getUserJsmLicenceState(tenantContext);
	const hasInsightPermission = useInsightVisibility();
	const userId = useAccountId();
	const { data: orgId } = useOrgId();
	const { isAssetsDataManagerUnauthorizedUser } = useAssetsDataManagerUserAuth();

	useEffect(() => {
		if (onExperienceFailure && fg('assets_sla_for_view_page_experiences')) {
			if (workspaceId === null && !loading && !error) {
				onExperienceFailure(
					'assets-app-base',
					getErrorAttributes(new Error('Workspace ID is not set')),
				);
			} else if (error && !isClientNetworkError(error) && !isHttpClientErrorResponse(error)) {
				onExperienceFailure('assets-app-base', getErrorAttributes(error));
			}
		}
	}, [onExperienceFailure, workspaceId, loading, error]);

	if (!isCMDBEnabledInEnvironment()) {
		return <NotFoundErrorPageAsync />;
	}

	if (!licenceState.siteHasJsmInstalled || !userId) {
		return <Redirect to="/" />;
	}

	if (!licenceState.siteIsPremium) {
		return <AsyncCmdbUpsellPage />;
	}

	if (!licenceState.userLicensed) {
		return <NoAccessErrorPage />;
	}

	if (workspaceId == null) {
		return !loading && !error ? <NotFoundErrorPageAsync /> : null;
	}

	if (!hasInsightPermission || error) {
		return <GenericPermissionErrorPageAsync />;
	}

	if (isAssetsDataManagerUnauthorizedUser) {
		return <NoAccessErrorPage />;
	}

	return (
		<InsightGlobalConfigContainer
			isGlobal
			workspaceId={workspaceId}
			createAnalyticsEvent={createAnalyticsEvent}
			userId={userId}
			orgId={orgId}
		>
			<PageWrapper>
				<CmdbArchitectureContainer workspaceId={workspaceId}>
					{children({ workspaceId })}
				</CmdbArchitectureContainer>
			</PageWrapper>
		</InsightGlobalConfigContainer>
	);
};

// Clean this up while cleaning assets_as_an_app_v2 ff
export const InsightAppBaseV2 = ({ children }: LegacyProps) => {
	const { workspaceId, error, loading } = useWorkspaceContext();
	const hasInsightPermission = useInsightVisibility();
	const tenantContext = useTenantContext();

	if (!isCMDBEnabledInEnvironment()) {
		return <NotFoundErrorPageAsync />;
	}

	if (!isLicensedPremiumUser(tenantContext)) {
		return <AsyncCmdbUpsellPage />;
	}

	if (workspaceId == null) {
		return !loading && !error ? <NotFoundErrorPageAsync /> : null;
	}

	if (hasInsightPermission === false || error) {
		return <GenericPermissionErrorPageAsync />;
	}

	return (
		<PageDI>
			<CmdbArchitectureContainer workspaceId={workspaceId}>
				{children({ workspaceId })}
			</CmdbArchitectureContainer>
		</PageDI>
	);
};
